import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { USER_ROLE, type CreateUserInput, type User, type UserRole } from '@etnrlz/shared';
import { api } from '../lib/api';
import { qk, useDeleteUser, useUsers } from '../lib/queries';
import { Badge, Button, Card, EmptyState, Field, Input, Loading, PageHeader, Select } from '../components/ui';
import { Modal } from '../components/Modal';
import { Icon } from '../components/icons';
import { initials } from '../lib/format';
import { canManage, useAuth } from '../auth/AuthContext';

const roleColor: Record<UserRole, string> = {
  ADMIN: '#c8ff00',
  GERENTE: '#3b82f6',
  MECANICO: '#f59e0b',
  VISUALIZADOR: '#94a3b8',
};

export function UsersPage() {
  const { data, isLoading } = useUsers();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const canDelete = canManage(user?.role);

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Usuários & permissões"
        actions={
          <Button onClick={() => setOpen(true)}>
            <Icon name="plus" className="h-4 w-4" /> Novo usuário
          </Button>
        }
      />
      {isLoading ? (
        <Loading />
      ) : !data || data.length === 0 ? (
        <EmptyState title="Nenhum usuário" />
      ) : (
        <Card className="p-0 divide-y divide-border">
          {data.map((u) => (
            <div key={u.id} className="flex items-center gap-3 px-4 py-3">
              <div className="h-9 w-9 rounded-full bg-accent/15 text-accent grid place-items-center text-sm font-bold">
                {initials(u.nome)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{u.nome}</div>
                <div className="text-xs text-muted truncate">{u.email}</div>
              </div>
              {!u.ativo && <span className="text-xs text-muted">inativo</span>}
              <Badge color={roleColor[u.papel]}>{u.papel}</Badge>
              {canDelete && <EditUserButton user={u} />}
              {canDelete && (
                <DeleteUserButton user={u} isSelf={!!user?.email && u.email === user.email} />
              )}
            </div>
          ))}
        </Card>
      )}
      <NewUserModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

interface EditForm {
  nome: string;
  email: string;
  papel: UserRole;
  ativo: boolean;
  senha: string;
}

function EditUserButton({ user }: { user: User }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const initial = (): EditForm => ({
    nome: user.nome,
    email: user.email,
    papel: user.papel,
    ativo: user.ativo,
    senha: '',
  });
  const [f, setF] = useState<EditForm>(initial);
  const set = (patch: Partial<EditForm>) => setF((s) => ({ ...s, ...patch }));

  const openModal = () => {
    setF(initial());
    setErr(null);
    setOpen(true);
  };

  const senhaInvalida = f.senha.length > 0 && f.senha.length < 8;

  const submit = async () => {
    setSaving(true);
    setErr(null);
    try {
      await api.put(`/users/${user.id}`, {
        nome: f.nome,
        email: f.email,
        papel: f.papel,
        ativo: f.ativo,
      });
      if (f.senha) await api.put(`/users/${user.id}/password`, { password: f.senha });
      await qc.invalidateQueries({ queryKey: qk.users });
      setOpen(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        onClick={openModal}
        className="text-xs text-muted hover:text-accent px-2 py-1 rounded transition-colors"
        title="Editar usuário"
      >
        Editar
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`Editar ${user.nome}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={submit} disabled={saving || !f.nome || !f.email || senhaInvalida}>
              {saving ? 'Salvando…' : 'Salvar'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Nome">
            <Input value={f.nome} onChange={(e) => set({ nome: e.target.value })} />
          </Field>
          <Field label="E-mail (login)">
            <Input type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} />
          </Field>
          <Field label="Papel">
            <Select value={f.papel} onChange={(e) => set({ papel: e.target.value as UserRole })}>
              {USER_ROLE.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </Field>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={f.ativo}
              onChange={(e) => set({ ativo: e.target.checked })}
              className="h-4 w-4 accent-accent"
            />
            Ativo
          </label>
          <Field
            label="Nova senha (opcional)"
            hint="Preencha para (re)definir o login. Mín. 8 caracteres. Em branco = não altera."
          >
            <Input
              type="password"
              value={f.senha}
              onChange={(e) => set({ senha: e.target.value })}
              placeholder="deixe em branco para não alterar a senha"
            />
          </Field>
          {senhaInvalida && <p className="text-st-warn text-sm">A senha precisa de ao menos 8 caracteres.</p>}
          {err && <p className="text-st-danger text-sm">{err}</p>}
        </div>
      </Modal>
    </>
  );
}

function DeleteUserButton({ user, isSelf }: { user: User; isSelf: boolean }) {
  const del = useDeleteUser();
  const [confirm, setConfirm] = useState(false);

  if (isSelf) {
    return (
      <span className="text-[11px] text-muted px-1" title="Você não pode remover a si mesmo">
        você
      </span>
    );
  }

  return (
    <>
      <button
        onClick={() => setConfirm(true)}
        className="text-muted hover:text-st-danger p-1 rounded transition-colors"
        title="Remover usuário"
        aria-label={`Remover ${user.nome}`}
      >
        <Icon name="trash" className="h-4 w-4" />
      </button>
      <Modal
        open={confirm}
        onClose={() => setConfirm(false)}
        title="Remover usuário"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(false)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              disabled={del.isPending}
              onClick={async () => {
                await del.mutateAsync(user.id);
                setConfirm(false);
              }}
            >
              {del.isPending ? 'Removendo…' : 'Remover'}
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Remover <span className="font-medium">{user.nome}</span> ({user.email})? Esta ação não pode
          ser desfeita.
        </p>
      </Modal>
    </>
  );
}

type NewUserForm = CreateUserInput & { senha?: string };

function NewUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<NewUserForm>({ nome: '', email: '', papel: 'MECANICO' });
  const [saving, setSaving] = useState(false);
  const set = (k: keyof NewUserForm, v: unknown) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post('/users', f);
      await qc.invalidateQueries({ queryKey: qk.users });
      onClose();
      setF({ nome: '', email: '', papel: 'MECANICO' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Novo usuário"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={!f.nome || !f.email || saving || (!!f.senha && f.senha.length < 8)}
          >
            Salvar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Nome"><Input value={f.nome} onChange={(e) => set('nome', e.target.value)} /></Field>
        <Field label="E-mail (login)" hint="Usado para entrar no sistema. Pode ser um e-mail de trabalho.">
          <Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} />
        </Field>
        <Field label="Papel">
          <Select value={f.papel} onChange={(e) => set('papel', e.target.value as UserRole)}>
            {USER_ROLE.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </Select>
        </Field>
        <Field label="Senha (opcional)" hint="Se preenchida, cria o login do usuário (mín. 8 caracteres).">
          <Input
            type="password"
            value={f.senha ?? ''}
            onChange={(e) => set('senha', e.target.value)}
            placeholder="deixe em branco para criar só o cadastro"
          />
        </Field>
      </div>
    </Modal>
  );
}
